import React, { useEffect, useMemo, useState } from 'react';
import { Target, Users2, MapPin, Plus, X } from 'lucide-react';
import { api } from '../../lib/api';
import { CONTACT_ROLE_OPTIONS, contactRoleLabel } from '../../lib/outreach';

// CampaignTargetingPanel — Sprint F item 6.3.
//
// Renders above the step editor on /admin/outreach/:id. Edit fields:
//   * target_lifecycle_stages (multi-select chips)
//   * target_account_filter   (borough / district / type)
//   * target_contact_role     (single select)
//   * auto_enroll             (toggle)
//   * custom_tokens_json      (key-value editor)
//
// Live eligibility count via /eligible-accounts.

const STAGES = ['prospect', 'contacted', 'engaged', 'verbal_yes', 'contracted', 'maintained', 'dormant', 'closed_lost'];
const ROLES = CONTACT_ROLE_OPTIONS.map((r) => r.value);
// Single-letter codes are the storage format (matches a.type_school_borough);
// labels are what we render in the dropdown.
const BOROUGHS = [
  { code: '',  label: 'Any' },
  { code: 'M', label: 'Manhattan' },
  { code: 'X', label: 'Bronx' },
  { code: 'K', label: 'Brooklyn' },
  { code: 'Q', label: 'Queens' },
  { code: 'R', label: 'Staten Island' },
];

function tryParseJson(s, fallback) {
  if (!s) return fallback;
  try { return JSON.parse(s); } catch { return fallback; }
}

export default function CampaignTargetingPanel({ campaign, onSaved }) {
  const [stages, setStages] = useState([]);
  const [filter, setFilter] = useState({ borough: '', district: '', type: '' });
  // Sprint K — multi-role targeting + combined-recipient toggle.
  // contactRoles is the new source of truth; the singular contactRole
  // field is kept in sync (= roles[0]) for back-compat.
  const [contactRoles, setContactRoles] = useState([]);
  const [combineRecipients, setCombineRecipients] = useState(false);
  // NOTE: auto_enroll editing moved to the Enroll Accounts page (Sprint K).
  // Keeping it here as well duplicated the affordance and made operators
  // think Targeting controlled enrollment behaviour.
  const [customTokens, setCustomTokens] = useState([]);
  const [eligibility, setEligibility] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [fromUserId, setFromUserId] = useState('');

  useEffect(() => {
    if (!campaign) return;
    setStages(tryParseJson(campaign.targetLifecycleStagesJson, []) || []);
    const f = tryParseJson(campaign.targetAccountFilterJson, {}) || {};
    setFilter({ borough: f.borough || '', district: f.district || '', type: f.type || '' });
    // Hydrate roles from the new JSON array first; fall back to the legacy
    // single-role field so pre-migration campaigns still load correctly.
    const rolesArr = tryParseJson(campaign.targetContactRolesJson, null);
    if (Array.isArray(rolesArr) && rolesArr.length > 0) setContactRoles(rolesArr);
    else if (campaign.targetContactRole) setContactRoles([campaign.targetContactRole]);
    else setContactRoles([]);
    setCombineRecipients(!!campaign.combineRecipients);
    const ct = tryParseJson(campaign.customTokensJson, {}) || {};
    setCustomTokens(Object.entries(ct).map(([k, v]) => ({ key: k, value: String(v) })));
    setFromUserId(campaign.fromUserId || '');
  }, [campaign]);

  useEffect(() => {
    let alive = true;
    // Same fix as CampaignWizard: /api/admin/users?role=X takes a single
    // value, so comma-separated returned 0 users; and the @nexsav.net
    // restriction hid valid local-dev senders. Filter client-side and
    // accept any admin/ops user — Resend reports verification errors
    // clearly at send time.
    api.get('/api/admin/users')
      .then((d) => {
        if (!alive) return;
        // Restrict senders to verified-in-Resend nexsav.net domain. See
        // CampaignWizard.jsx for the same gate + reasoning.
        const list = (d.users || []).filter(
          (u) =>
            (u.role === 'nexsav_admin' || u.role === 'ops') &&
            /@nexsav\.net$/i.test(u.email || ''),
        );
        setUsers(list);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const refreshEligibility = React.useCallback(() => {
    if (!campaign?.id) return;
    api.get(`/api/admin/outreach/campaigns/${campaign.id}/eligible-accounts`)
      .then((d) => setEligibility(d))
      .catch((err) => setError(err?.data?.error || err?.message || ''));
  }, [campaign?.id]);

  useEffect(() => { refreshEligibility(); }, [refreshEligibility, savedAt]);

  const customTokensObject = useMemo(() => {
    const out = {};
    for (const r of customTokens) if (r.key.trim()) out[r.key.trim()] = r.value;
    return out;
  }, [customTokens]);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await api.patch(`/api/admin/outreach/campaigns/${campaign.id}`, {
        targetLifecycleStagesJson: JSON.stringify(stages),
        targetAccountFilterJson: JSON.stringify(filter),
        // Sprint K — write the new array shape; mirror the first role to
        // the legacy field for back-compat.
        targetContactRolesJson: JSON.stringify(contactRoles),
        targetContactRole: contactRoles[0] || null,
        combineRecipients,
        customTokensJson: JSON.stringify(customTokensObject),
        fromUserId: fromUserId || null,
      });
      setSavedAt(Date.now());
      onSaved?.();
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Failed to save targeting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="campaign-targeting-panel" className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-500">
        <Target className="w-3 h-3" />
        Targeting
      </div>

      {/* Sender */}
      <Field label="Send as">
        <select
          data-testid="campaign-from-user"
          value={fromUserId}
          onChange={(e) => setFromUserId(e.target.value)}
          className={selectCls}
        >
          <option value="">Select a NexSav sender…</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name} &lt;{u.email}&gt;</option>)}
        </select>
      </Field>

      {/* Stages */}
      <Field label="Lifecycle stages">
        <div className="flex flex-wrap gap-1.5" data-testid="campaign-stages">
          {STAGES.map((s) => {
            const active = stages.includes(s);
            return (
              <button
                key={s}
                type="button"
                data-testid={`campaign-stage-${s}`}
                onClick={() => setStages((prev) => active ? prev.filter((x) => x !== s) : [...prev, s])}
                className={`px-2.5 py-1 rounded-full text-[11px] border ${active ? 'bg-[#B0FC38]/15 border-[#B0FC38]/40 text-white' : 'border-white/[0.08] text-gray-400 hover:text-white'}`}
              >
                {s.replace(/_/g, ' ')}
              </button>
            );
          })}
        </div>
      </Field>

      {/* Filter */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Borough">
          <select data-testid="campaign-filter-borough" value={filter.borough} onChange={(e) => setFilter({ ...filter, borough: e.target.value })} className={selectCls}>
            {BOROUGHS.map((b) => <option key={b.code || 'any'} value={b.code}>{b.label}</option>)}
          </select>
        </Field>
        <Field label="District">
          <input data-testid="campaign-filter-district" value={filter.district} onChange={(e) => setFilter({ ...filter, district: e.target.value })} className={inputCls} placeholder="9" />
        </Field>
        <Field label="Account type">
          <select data-testid="campaign-filter-type" value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })} className={selectCls}>
            <option value="">Any</option>
            <option value="school">school</option>
            <option value="external">external</option>
            <option value="district">district</option>
          </select>
        </Field>
      </div>

      {/* Contact roles — multi-select chips */}
      <Field label="Recipient roles">
        <div className="flex flex-wrap gap-1.5" data-testid="campaign-contact-roles">
          {ROLES.map((r) => {
            const active = contactRoles.includes(r);
            return (
              <button
                key={r}
                type="button"
                data-testid={`campaign-contact-role-${r}`}
                onClick={() => setContactRoles((prev) => active ? prev.filter((x) => x !== r) : [...prev, r])}
                className={`px-2.5 py-1 rounded-full text-[11px] border ${active ? 'bg-[#B0FC38]/15 border-[#B0FC38]/40 text-white' : 'border-white/[0.08] text-gray-400 hover:text-white'}`}
              >
                {contactRoleLabel(r)}
              </button>
            );
          })}
        </div>
      </Field>

      {/* Combine recipients */}
      <label className={`block rounded-lg border p-2.5 cursor-pointer transition-colors ${
        combineRecipients ? 'border-[#B0FC38]/40 bg-[#B0FC38]/[0.06]' : 'border-white/[0.06] bg-white/[0.015] hover:border-white/[0.12]'
      }`}>
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            data-testid="campaign-combine-recipients"
            checked={combineRecipients}
            onChange={(e) => setCombineRecipients(e.target.checked)}
            className="accent-[#B0FC38] mt-0.5"
          />
          <div className="flex-1 min-w-0">
            <div className="text-[12px] text-white">Send one combined email per account</div>
            <div className="text-[10.5px] text-gray-400 mt-0.5 leading-relaxed">
              All matching contacts at the account go into the TO list of one email so they share a reply thread. Off (default): one email per account to the primary matching contact.
            </div>
          </div>
        </div>
      </label>

      {/* Custom tokens */}
      <div>
        <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-gray-400/80 mb-1.5">
          <span>Custom tokens</span>
          <button
            type="button"
            data-testid="campaign-custom-token-add"
            onClick={() => setCustomTokens([...customTokens, { key: '', value: '' }])}
            className="text-gray-300 hover:text-white inline-flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
        {customTokens.map((row, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 mb-1.5" data-testid={`campaign-custom-token-${idx}`}>
            <input
              type="text"
              placeholder="key"
              value={row.key}
              onChange={(e) => setCustomTokens((prev) => prev.map((r, i) => i === idx ? { ...r, key: e.target.value } : r))}
              className="col-span-4 px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-sm text-white"
            />
            <input
              type="text"
              placeholder="value"
              value={row.value}
              onChange={(e) => setCustomTokens((prev) => prev.map((r, i) => i === idx ? { ...r, value: e.target.value } : r))}
              className="col-span-7 px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-sm text-white"
            />
            <button
              type="button"
              onClick={() => setCustomTokens((prev) => prev.filter((_, i) => i !== idx))}
              className="col-span-1 p-1 text-gray-500 hover:text-red-300"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-white/[0.04]">
        <div data-testid="targeting-eligibility" className="text-[12px] text-gray-300">
          {eligibility ? (
            <span><Users2 className="inline w-3 h-3 mr-1" /> {eligibility.total} eligible accounts</span>
          ) : (
            <span className="text-gray-500">Loading eligibility…</span>
          )}
          {eligibility?.by_borough && Object.keys(eligibility.by_borough).length > 0 && (
            <span className="ml-3 text-[11px] text-gray-500">
              <MapPin className="inline w-3 h-3 mr-1" />
              {Object.entries(eligibility.by_borough).map(([k, v]) => `${k}: ${v}`).join(' · ')}
            </span>
          )}
        </div>
        <button
          type="button"
          data-testid="campaign-targeting-save"
          onClick={save}
          disabled={saving}
          className="px-4 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save targeting'}
        </button>
      </div>
      {error && <div className="text-[12px] text-red-300/90">{error}</div>}
    </div>
  );
}

const inputCls = 'w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white';
const selectCls = inputCls;

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-gray-400/80 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
